import React from "react";
import { Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MobileItem from "./MobileItem";
import OnlyOneTapLogo from "../../assets/ootfooter.svg";
import { homePathFor, useAuth } from "../../context/AuthContext";
import LoginRequired from "../common/LoginRequired";

interface MobileNavBarProps {
    active: string;
    onSearchClick: () => void;
}

// Tab 3 is role-driven:
//   Guest (null) or CUSTOMER   → Favourites
//   SERVICE_PROVIDER           → Dashboard
//   INTERNAL_USER/FIELD_OP     → Admin
// On logout user becomes null → automatically reverts to Favourites tab.
const getRoleTab = (userType: string | null | undefined, active: string) => {
    if (userType === "SERVICE_PROVIDER") {
        return {
            icon: <BarChartOutlinedIcon />,
            label: "footer.dashboard",
            active: active === "Dashboard",
            path: "/account/dashboard",
        };
    }
    if (userType === "INTERNAL_USER" || userType === "FIELD_OPERATOR") {
        return {
            icon: <AdminPanelSettingsIcon />,
            label: "footer.admin",
            active: active === "Admin",
            path: "/admin/dashboard",
        };
    }
    // Guest (null/undefined) and CUSTOMER both get Favourites
    return {
        icon: active === "Favorites" ? <FavoriteIcon /> : <FavoriteBorderIcon />,
        label: "myaccount.myFavorites",
        active: active === "Favorites",
        path: "/favorites",
    };
};

const MobileNavBar: React.FC<MobileNavBarProps> = ({ active, onSearchClick }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, isAuthenticated, isLoading } = useAuth();
    const roleTab = getRoleTab(user?.userType, active);
    const [loginOpen, setLoginOpen] = React.useState(false);

    // Guard navigation to protected tabs for guests.
    // While auth state is still loading we allow navigation rather than
    // incorrectly treating the user as a guest (isAuthenticated is false
    // during the initial React Query fetch even for logged-in users).
    const handleProtectedNav = (path: string) => {
        console.log("[MobileNavBar] handleProtectedNav", { path, isAuthenticated, isLoading, userType: user?.userType });
        if (isLoading) {
            // Auth not settled yet — navigate and let the route guard decide
            navigate(path);
        } else if (!isAuthenticated) {
            setLoginOpen(true);
        } else {
            navigate(path);
        }
    };

    return (
        <>
            {/* Spacer so page content isn't hidden behind the fixed bar */}
            <Box sx={{ minHeight: 40 }} />

            <LoginRequired open={loginOpen} onClose={() => setLoginOpen(false)} />

            <Box
                position="fixed"
                bottom={0}
                left={0}
                width="100%"
                zIndex={1100}
                bgcolor="information.main"
                display="flex"
                justifyContent="space-around"
                alignItems="center"
                sx={{
                    minHeight: 40,
                    borderTop: "1px solid",
                    borderColor: "primary.main",
                }}
            >
                <MobileItem
                    icon={
                        <Box
                            component="img"
                            src={OnlyOneTapLogo}
                            alt="OnlyOneTap logo"
                            sx={{ height: 24, width: 24, objectFit: "contain" }}
                        />
                    }
                    label={t("footer.home")}
                    active={active === "Home"}
                    onClick={() => navigate(homePathFor(user?.userType))}
                />

                <MobileItem
                    icon={<SearchIcon />}
                    label={t("footer.search")}
                    active={active === "Search"}
                    onClick={onSearchClick}
                />

                {/* Role-driven third tab — Favorites (customer/guest), Dashboard
                    (service provider), or Admin (internal/field operator).
                    Guests see the LoginRequired screen on tap. */}
                <MobileItem
                    icon={roleTab.icon}
                    label={t(roleTab.label)}
                    active={roleTab.active}
                    onClick={() => handleProtectedNav(roleTab.path)}
                />

                {/* Help page — for everyone, a real link so it's crawlable */}
                <MobileItem
                    icon={<HelpOutlineIcon />}
                    label={t("helpCenter.help")}
                    active={active === "Help"}
                    to="/content/help"
                />

                <MobileItem
                    icon={<PersonOutlineIcon />}
                    label={t("footer.account")}
                    active={active === "Account"}
                    onClick={() => handleProtectedNav("/account")}
                />
            </Box>
        </>
    );
};

export default MobileNavBar;
